import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

const CaseStatisticsPage = ({ embedded = false, residentId: propResidentId }) => {
  // Use propResidentId if provided (for embedded), else use useParams
  const params = useParams();
  const residentId = propResidentId || params.residentId;
  const [caseStats, setCaseStats] = useState(null);
  const [persistedCaseTrends, setPersistedCaseTrends] = useState({});
  const [residentInfo, setResidentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCaseStatistics = async () => {
      const token = localStorage.getItem("token");
      const userRole = localStorage.getItem("role");
      
      try {
        setLoading(true);
        
        // Determine the API endpoint based on user role and residentId
        let apiEndpoint;
        if (userRole === 'director') {
          apiEndpoint = `${API_ENDPOINTS.DIRECTOR}/resident-case-statistics/${residentId}`;
        } else {
          // For residents, always use their own statistics endpoint
          apiEndpoint = `${API_ENDPOINTS.RESIDENT}/case-statistics`;
        }

        const response = await fetch(apiEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch case statistics');
        }

        const data = await response.json();
        
        if (userRole === 'director') {
          // For directors, the data is already in statistics format with trends
          setCaseStats(data);
          // Fetch resident info
          const residentResponse = await fetch(`${API_ENDPOINTS.DIRECTOR}/residents-case-summary`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (residentResponse.ok) {
            const residents = await residentResponse.json();
            const resident = residents.find(r => r.id === parseInt(residentId));
            if (resident) {
              setResidentInfo(resident);
            }
          }
        } else {
          // For residents, the data is already in statistics format
          setCaseStats(data);
          setResidentInfo({
            name: `${localStorage.getItem("firstName")} ${localStorage.getItem("lastName")}`,
            year: localStorage.getItem("year")
          });
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching case statistics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStatistics();
  }, [residentId]);

  // Preserve last non-neutral trend until replaced by a new change
  useEffect(() => {
    const nextTrends = caseStats?.trends;
    if (!nextTrends || typeof nextTrends !== "object") return;

    setPersistedCaseTrends((prev) => {
      const updated = { ...prev };
      Object.entries(nextTrends).forEach(([key, trend]) => {
        if (trend === "up" || trend === "down") {
          updated[key] = trend;
        }
      });
      return updated;
    });
  }, [caseStats]);

  const calculateCaseStats = (logs) => {
    if (!logs || logs.length === 0) {
      setCaseStats(null);
      return;
    }
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const academicYearStart = new Date(now.getFullYear(), 6, 1); // July 1st
    if (now.getMonth() < 6) {
      academicYearStart.setFullYear(academicYearStart.getFullYear() - 1);
    }
    const stats = {
      totalCases: logs.length,
      recentCases: logs.filter(log => new Date(log.date) >= thirtyDaysAgo).length,
      roboticCases: logs.filter(log => log.robotic === true).length,
      minimallyInvasiveCases: logs.filter(log => log.minimallyInvasive === true).length,
      openCases: logs.filter(log => log.robotic === false && log.minimallyInvasive === false).length,
      thisAcademicYearCases: logs.filter(log => new Date(log.date) >= academicYearStart).length,
      chartData: {
        rotation: [],
        role: [],
        monthly: []
      },
      topAttendings: []
    };
    // Calculate rotation data
    const rotationCounts = {};
    logs.forEach(log => {
      const rotation = log.rotation || 'Unknown';
      rotationCounts[rotation] = (rotationCounts[rotation] || 0) + 1;
    });
    stats.chartData.rotation = Object.entries(rotationCounts).map(([name, value]) => ({ name, value }));
    // Calculate role data
    const roleCounts = {};
    logs.forEach(log => {
      const role = log.role || 'Unknown';
      roleCounts[role] = (roleCounts[role] || 0) + 1;
    });
    stats.chartData.role = Object.entries(roleCounts).map(([name, value]) => ({ name, value }));
    // Calculate monthly data (last 12 months)
    const monthlyCounts = {};
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      monthlyCounts[monthKey] = 0;
    }
    logs.forEach(log => {
      const logDate = new Date(log.date);
      const monthKey = logDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (monthlyCounts.hasOwnProperty(monthKey)) {
        monthlyCounts[monthKey]++;
      }
    });
    stats.chartData.monthly = Object.entries(monthlyCounts).map(([name, value]) => ({ name, value }));
    // Calculate top attendings
    const attendingCounts = {};
    logs.forEach(log => {
      const attending = log.attending || 'Unknown';
      attendingCounts[attending] = (attendingCounts[attending] || 0) + 1;
    });
    stats.topAttendings = Object.entries(attendingCounts)
      .map(([attending, count]) => ({ attending, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    setCaseStats(stats);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={embedded ? "" : "bg-light min-vh-100"}>
        <div className={embedded ? "" : "container"}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: embedded ? '200px' : '60vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading case statistics...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? "" : "bg-light min-vh-100"}>
        <div className={embedded ? "" : "container"}>
          <div className="alert alert-danger mt-4" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "bg-light min-vh-100"}>
      {!embedded && <PageTitle title="Case Statistics" description="View case log statistics and performance metrics" />}
      <div className={embedded ? "" : "container"}>
        <div className={embedded ? "" : "py-4"}>
          {/* Header */}
          {!embedded && (
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">Case Statistics</h2>
                {residentInfo && (
                  <p className="text-muted mb-0">
                    {residentInfo.name} • PGY-{residentInfo.year} • {caseStats?.totalCases || 0} Total Cases
                  </p>
                )}
              </div>
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back
              </button>
            </div>
          )}

          {!caseStats ? (
            <div className="text-center py-5">
              <div>
                <img src="/images/undraw_no-data_ig65.svg" alt="No Case Data Available" style={{ height: '200px'}}/>
                <h4 className="mt-4">No Case Data Available</h4>
                <p>No case logs have been recorded yet.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Statistics - Hide when embedded in ResidentDashboard */}
              {(!embedded || window.location.pathname !== '/resident/dashboard') && (
                <div className="d-flex justify-content-between align-items-start gap-5 flex-wrap mb-4 border-bottom">
                  <div className="flex-fill p-2 mb-md-3 ">
                    <div className="text-muted small mb-1">Total Cases</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.totalCases || 0}</span>
                        {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-file-medical text-muted opacity-50"></i>
                    </div>
                  </div>
                  <div className="flex-fill p-2 mb-md-3  ">
                    <div className="text-muted small mb-1">Last 30 Days</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.recentCases || 0}</span>
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-calendar-check text-muted opacity-50"></i>
                    </div>
                  </div>
                  <div className="flex-fill p-2 mb-md-3 ">
                    <div className="text-muted small mb-1">Robotic Cases</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.roboticCases || 0}</span>
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-robot text-muted opacity-50"></i>
                    </div>
                  </div>
                  <div className="flex-fill p-2 mb-md-3 ">
                    <div className="text-muted small mb-1">Min. Invasive</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.minimallyInvasiveCases || 0}</span>
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-scissors text-muted opacity-50"></i>
                    </div>
                  </div>
                  <div className="flex-fill p-2 mb-md-3 ">
                    <div className="text-muted small mb-1">Open Cases</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.openCases || 0}</span>
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-person-lines-fill text-muted opacity-50"></i>
                    </div>
                  </div>
                  <div className="flex-fill p-2 mb-md-3 ">
                    <div className="text-muted small mb-1">Academic Year</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.thisAcademicYearCases || 0}</span>
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                      </div>
                      <i className="bi bi-calendar-event text-muted opacity-50"></i>
                    </div>
                  </div>
                </div>
              )}

              {/* Charts */}
              <div className="row">
                <div className="col-lg-6 col-md-12 mb-4 pe-0">
                  <div className="p-4">
                    <h6 className="text-muted mb-4 fw-semibold" style={{ fontSize: '0.875rem', letterSpacing: '0.5px' }}>
                      Cases by Rotation
                    </h6>
                    {caseStats.chartData && caseStats.chartData.rotation && caseStats.chartData.rotation.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={caseStats.chartData.rotation} margin={{ top: 0, right: 0, left: 0, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="1 6" stroke="var(--border-bg)" strokeWidth={1} vertical={false} />
                          <XAxis 
                            dataKey="name" 
                            angle={-45} 
                            textAnchor="end" 
                            height={80}
                            tick={{ fontSize: '0.75rem', fill: 'var(--gray-600)', fontFamily: 'inherit' }}
                            axisLine={{stroke: 'var(--border-bg)'}}
                            tickLine={false}
                          />
                          <YAxis 
                            tick={{ fontSize: '0.75rem', fill: 'var(--gray-600)', fontFamily: 'inherit' }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'var(--card-bg)',
                              border: '1px solid var(--border-bg)',
                              borderRadius: '6px',
                              boxShadow: 'var(--box-shadow-sm)',
                              padding: '8px 12px',
                              fontSize: '0.875rem'
                            }}
                            cursor={{ fill: 'var(--gray-100)' }}
                          />
                          <Bar 
                            dataKey="value" 
                            fill="var(--indigo)"
                            radius={[3, 3, 0, 0]}
                            barSize={36}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="bi bi-bar-chart" style={{ fontSize: 48, opacity: 0.2 }}></i>
                        <p className="mt-2 small">No rotation data available</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="col-lg-6 col-md-12 mb-4 ps-0" style={{ borderLeft: '1px dashed var(--border-bg)' }}>
                  <div className="p-4 ">
                    <h6 className="text-muted mb-4 fw-semibold" style={{ fontSize: '0.875rem', letterSpacing: '0.5px' }}>
                      Cases by Role
                    </h6>
                    {caseStats.chartData && caseStats.chartData.role && caseStats.chartData.role.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart strokeWidth={0}>
                          <Pie
                            data={caseStats.chartData.role}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            innerRadius={50}
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                            labelStyle={{ fontSize: '0.75rem', fontWeight: '500', fill: 'var(--gray-800)' }}
                          >
                            {caseStats.chartData.role.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={[
                                  '#6366f1', // Indigo
                                  '#8b5cf6', // Purple
                                  '#ec4899', // Pink
                                  '#f43f5e', // Rose
                                  '#f59e0b', // Amber
                                  '#10b981', // Emerald
                                  '#06b6d4', // Cyan
                                  '#3b82f6', // Blue
                                  '#a855f7', // Violet
                                  '#14b8a6', // Teal
                                  '#ef4444', // Red
                                  '#84cc16', // Lime
                                  '#f97316', // Orange
                                  '#22c55e', // Green
                                  '#0ea5e9', // Sky
                                ][index % 15]} 
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'var(--card-bg)',
                              border: '1px solid var(--border-bg)',
                              borderRadius: '6px',
                              boxShadow: 'var(--box-shadow-sm)',
                              padding: '8px 12px',
                              fontSize: '0.875rem'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="bi bi-pie-chart" style={{ fontSize: 48, opacity: 0.2 }}></i>
                        <p className="mt-2 small">No role data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Attendings */}
              {caseStats.topAttendings && caseStats.topAttendings.length > 0 && (
                <div className="p-4 mb-4" style={{ borderTop: '1px dashed var(--border-bg)', borderBottom: '1px dashed var(--border-bg)' }}>
                  <h6 className="text-muted mb-4 fw-semibold" style={{ fontSize: '0.875rem', letterSpacing: '0.5px' }}>
                    Top Attendings
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {caseStats.topAttendings.map((attending, index) => (
                      <div key={index} className="d-flex align-items-center rounded px-3 py-2 border">
                        <span className="fw-medium me-2" style={{ fontSize: '0.875rem' }}>{attending.attending}</span>
                        <span className="badge rounded-pill bg-primary py-1 px-2">{attending.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Trend Chart */}
              {caseStats.chartData && caseStats.chartData.monthly && caseStats.chartData.monthly.some(item => item.value > 0) && (
                <div className="p-4">
                  <h6 className="text-muted mb-4 fw-semibold" style={{ fontSize: '0.875rem', letterSpacing: '0.5px' }}>
                    Monthly Case Trend (Last 12 Months)
                  </h6>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={caseStats.chartData.monthly} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="2 6" stroke="var(--border-bg)" strokeWidth={1} vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                        tick={{ fontSize: '0.75rem', fill: 'var(--gray-600)', fontFamily: 'inherit' }}
                        axisLine={{stroke: 'var(--border-bg)'}}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: '0.75rem', fill: 'var(--gray-600)', fontFamily: 'inherit' }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'var(--card-bg)',
                          border: '1px solid var(--border-bg)',
                          borderRadius: '6px',
                          boxShadow: 'var(--box-shadow-sm)',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                        cursor={{ fill: 'var(--gray-100)' }}
                      />
                      <Bar 
                        dataKey="value" 
                        fill="var(--green)"
                        radius={[6, 6, 0, 0]}
                        barSize={36}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseStatisticsPage; 